import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Pane,
  Card,
  Table,
  Button,
  SelectField,
  TextInputField,
  majorScale,
  Spinner,
  Alert,
} from 'evergreen-ui';
import { useCsvImport } from './hooks/useCsvImport';

// Create a separate component for the mapping row to prevent unnecessary re-renders
const MappingRow = React.memo(({ mapping, csvInfo, systemFields, onMappingChange }) => {
  // Determine the intent based on whether a system field is selected
  const rowIntent = !mapping.system_field ? 'warning' : 'none';

  return (
    // Apply the determined intent to the Table.Row
    <Table.Row key={mapping.id} intent={rowIntent}>
      <Table.TextCell>{mapping.csv_header}</Table.TextCell>
      <Table.TextCell>
        {csvInfo?.sample_data[0]?.[mapping.column_index] || ''}
      </Table.TextCell>
      <Table.Cell>
        <SelectField
          label=""
          value={mapping.system_field || ''}
          onChange={(e) => onMappingChange(mapping.id, 'system_field', e.target.value)}
        >
          <option value="">-- Not Mapped --</option>
          {systemFields.map(field => (
            <option key={field.name} value={field.name}>
              {field.name} - {field.description}
            </option>
          ))}
        </SelectField>
      </Table.Cell>
      <Table.Cell>
        <TextInputField
          label=""
          value={mapping.default_value || ''}
          onChange={(e) => onMappingChange(mapping.id, 'default_value', e.target.value)}
          placeholder="Default value (optional)"
        />
      </Table.Cell>
    </Table.Row>
  );
});

const FieldMapper = ({ importSession, onComplete, onError }) => {
  // Use refs to prevent re-renders
  const importSessionRef = useRef(importSession);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  // Update refs when props change
  useEffect(() => {
    importSessionRef.current = importSession;
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [importSession, onComplete, onError]);

  const [csvInfo, setCsvInfo] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [systemFields, setSystemFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const isMountedRef = useRef(true);
  const hasLoadedDataRef = useRef(false);

  // Get API functions from the hook
  const { getCsvInfo, suggestFieldMappings, updateFieldMappings, getSystemFields } = useCsvImport();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load data only once
  useEffect(() => {
    // Skip if we don't have a valid session ID or if we've already loaded data
    if (!importSessionRef.current?.id || hasLoadedDataRef.current) {
      return;
    }

    const fetchData = async () => {
      if (!isMountedRef.current) return;

      try {
        setIsLoading(true);

        // Get CSV info
        const info = await getCsvInfo(importSessionRef.current.id);
        if (!isMountedRef.current) return;
        setCsvInfo(info);

        // Get suggested mappings
        const suggestedMappings = await suggestFieldMappings(importSessionRef.current.id);
        if (!isMountedRef.current) return;

        // For product imports, automatically set isbn as the unique identifier if it exists
        if (importSessionRef.current.entity_type === 'product') {
          const updatedMappings = suggestedMappings.map(mapping => {
            if (mapping.system_field === 'isbn') {
              return { ...mapping, is_unique_identifier: true };
            }
            return mapping;
          });
          setMappings(updatedMappings);
        } else {
          setMappings(suggestedMappings);
        }

        // Get system fields for the entity type
        const fields = await getSystemFields(importSessionRef.current.entity_type);
        if (!isMountedRef.current) return;
        setSystemFields(fields);

        hasLoadedDataRef.current = true;
      } catch (error) {
        console.error('Error loading CSV data:', error);
        if (isMountedRef.current && onErrorRef.current) {
          onErrorRef.current(error.message || 'Failed to load CSV information. Please try again.');
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, []); // Empty dependency array - only run once on mount

  // Handle mapping change
  const handleMappingChange = useCallback((mappingId, field, value) => {
    setMappings(prevMappings => {
      // Regular field update
      return prevMappings.map(mapping =>
        mapping.id === mappingId
          ? {
              ...mapping,
              [field]: value,
              is_mapped: field === 'system_field' ? !!value : mapping.is_mapped,
              // If this is a product import and the field is being mapped to 'isbn', set is_unique_identifier to true
              is_unique_identifier: field === 'system_field' && value === 'isbn' && importSessionRef.current?.entity_type === 'product'
                ? true
                : mapping.is_unique_identifier
            }
          : mapping
      );
    });
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!importSessionRef.current?.id || !mappings.length) return;

    try {
      setIsSaving(true);
      await updateFieldMappings(importSessionRef.current.id, mappings);

      if (isMountedRef.current && onCompleteRef.current) {
        onCompleteRef.current();
      }
    } catch (error) {
      console.error('Error updating field mappings:', error);
      if (isMountedRef.current && onErrorRef.current) {
        onErrorRef.current(error.message || 'Failed to save field mappings. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [mappings, updateFieldMappings]);

  if (isLoading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height={200}>
        <Spinner />
      </Pane>
    );
  }

  return (
    <Pane>
      <Card
        backgroundColor="white"
        elevation={0}
        border="default"
        padding={majorScale(2)}
        marginBottom={majorScale(2)}
      >
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>CSV Header</Table.TextHeaderCell>
            <Table.TextHeaderCell>Sample Data</Table.TextHeaderCell>
            <Table.TextHeaderCell>System Field</Table.TextHeaderCell>
            <Table.TextHeaderCell>Default Value</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {mappings.map((mapping) => (
              <MappingRow
                key={mapping.id}
                mapping={mapping}
                csvInfo={csvInfo}
                systemFields={systemFields}
                onMappingChange={handleMappingChange}
              />
            ))}
          </Table.Body>
        </Table>
      </Card>

      <Pane display="flex" justifyContent="flex-end">
        <Button
          appearance="primary"
          onClick={handleSave}
          disabled={isSaving}
          marginRight={majorScale(2)}
        >
          {isSaving ? <Spinner size={16} /> : 'Save and Continue'}
        </Button>
      </Pane>
    </Pane>
  );
};

export default FieldMapper;
